import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SortDescendingOutlined, SortAscendingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';
import { AreaSlaData, DriverSlaData, WhsSlaData, DspSlaData, DetailData } from '../types';
import { LEVEL1_META, LEVEL2_META, getLevel1Label, getLevel2Label, getLevel2LabelInGroup } from '../undeliveredAttribution';
import {
    DriverFilterOption,
    UNASSIGNED_DRIVER_ID,
    buildDriverOptions,
    isUnassignedDriverOnly,
    rememberDriverNames,
    toDriverIds
} from '../../../../../utils/driverFilterOption';
import {
  useDeliveryProgressMonitoringOfSlaQuery,
  useDeliveryProgressMonitoringOfSlaDetailQuery
} from '../graphql/hooks/useDeliveryProgressMonitoringOfSlaQuery';
import {
  DeliveryProgressMonitoringOfSlaPageRequestType,
  DeliveryProgressMonitoringOfSlaDetailPageRequestType,
  DeliveryProgressMonitoringOfSlaFiltersType,
  GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
  GET_BREAK_DRIVER_SIDE_BREAKDOWN,
  type DspDriverBreakdownItemType,
  type NoUpdateBreakType
} from '../graphql/queries/getDeliveryProgressMonitoringOfSlaList';
import {
    SEARCH_DELIVERY_FILTER_AREA_OPTIONS,
    SEARCH_DELIVERY_FILTER_AREA_OPTIONS_FOR_DSP,
    SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS,
    SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS_FOR_DSP,
    SEARCH_DELIVERY_FILTER_DSP_OPTIONS,
    type SearchDeliveryFilterDspOptionsResponse,
    type SearchDeliveryFilterAreaOptionsResponse,
    type SearchDeliveryFilterAreaOptionsForDspResponse,
    type SearchDeliveryFilterDriverOptionsResponse,
    type SearchDeliveryFilterDriverOptionsForDspResponse,
    type DeliveryFilterOption,
} from '../graphql/queries/searchDeliveryFilterOptions';
import {
  GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
  GET_DSP_BREAK_DRIVER_SIDE_BREAKDOWN,
} from '../graphql/queries/getDspDeliveryProgressMonitoringOfSlaList';
import { useQuery, useApolloClient } from '@apollo/client';
import { useWarehouseContext } from '../../../../../contexts/WarehouseContext';
import { useCurrentDsp } from '../../../../../hooks/useCurrentDsp';
import { getDateRangeByPreset, type DateRangePreset } from '../components/SlaMonitoringFilter';
import {
  ARRIVAL_POINT_TYPE_WHS,
  DIMENSION_AREA,
  DIMENSION_DRIVER,
  parseArrivalPointTypeFromSearch,
  type ArrivalPointType
} from '../constants';
import {
    UndeliveredLevel1,
    UndeliveredLevel2,
    decodeUndeliveredAnomaly,
} from '../undeliveredAttribution';

// 扩展 dayjs 支持时区功能
dayjs.extend(utc);
dayjs.extend(timezone);

/** 与 SlaMonitoringTable 一致，表格可能传入任意维度行；Parcel 页仅使用 area/driver */
export type SlaTableRow = AreaSlaData | DriverSlaData | WhsSlaData | DspSlaData;
/** Parcel 页筛选器与表格仅支持区域/司机维度，与 SlaMonitoringFilter 一致 */
export type SlaMonitoringViewMode = typeof DIMENSION_AREA | typeof DIMENSION_DRIVER;

export interface SlaMonitoringFilters {
    dateRangePreset: DateRangePreset;
    dateRange: [dayjs.Dayjs, dayjs.Dayjs];
    arrivalDate: dayjs.Dayjs;
    selectedCities: string[];
    selectedDsps: string[];
    selectedAreas: string[];
    selectedDrivers: string[]; // 司机维度下主列表筛选：选中的司机名称
    arrivalPointType: ArrivalPointType;
    arrivalTimeDateRange: [dayjs.Dayjs, dayjs.Dayjs] | null; // 到达时间日期范围（限制过去60天，用于所有选项包括快捷选项和自定义）
    arrivalTimeDatePreset: 'today' | 'yesterday' | 'last7days' | 'last15days' | 'custom' | null; // 当前选择的快捷选项或自定义
    viewMode: SlaMonitoringViewMode;
}

export interface UseSlaMonitoringResult {
    filters: SlaMonitoringFilters;
    setFilters: (filters: SlaMonitoringFilters) => void;
    areaData: AreaSlaData[];
    driverData: DriverSlaData[];
    viewMode: SlaMonitoringViewMode;
    handleViewModeChange: (mode: SlaMonitoringViewMode) => void;
    anomalyData: Array<{ type: string; label: string; count: number; color: string }>;
    summaryData: any;
    dsps: Array<{ id: number; name: string }>;
    areas: Array<{ id: number; name: string }>;
    drivers: DriverFilterOption[];
    /** DSP/区域/司机下拉的远程搜索入口（选项已改为按关键词查主库，不再聚合 SLA 大表） */
    onDspSearch: (keyword: string) => void;
    onAreaSearch: (keyword: string) => void;
    onDriverSearch: (keyword: string) => void;
    dspOptionsLoading: boolean;
    areaOptionsLoading: boolean;
    driverOptionsLoading: boolean;
    /** 详情抽屉内司机远程搜索（与主列表同口径） */
    onDrawerDriverSearch: (keyword: string) => void;
    drawerDriverOptionsLoading: boolean;
    /** 详情抽屉用：有详情接口选项时用详情的，否则用主列表选项，避免抽屉内 DSP/区域/司机下拉为空 */
    detailDrawerDsps: Array<{ id: number; name: string }>;
    detailDrawerAreas: Array<{ id: number; name: string }>;
    detailDrawerDrivers: DriverFilterOption[];
    /** 详情抽屉用：DSP-司机级联后的司机下拉选项（选 DSP 时过滤；未选 DSP 全量） */
    drawerDrivers: DriverFilterOption[];
    detailFilters: {
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    };
    setDetailFilters: (filters: {
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    }) => void;
    drawerVisible: boolean;
    drawerTitle: string;
    drawerData: DetailData[];
    drawerColumns: any[];
    setDrawerVisible: (visible: boolean) => void;
    handleDetailClick: (type: string, record: SlaTableRow) => Promise<void>;
    handleAnomalyClick: (anomalyType: string, prefill?: { selectedDsps?: string[]; selectedAreas?: string[]; selectedDrivers?: string[] }) => void;
    breakdownDrawerVisible: boolean;
    setBreakdownDrawerVisible: (visible: boolean) => void;
    breakdownData: DspDriverBreakdownItemType[];
    breakdownTitle: string;
    breakdownAnomalyType: string;
    breakdownLoading: boolean;
    breakdownPage: number;
    breakdownPageSize: number;
    breakdownTotal: number;
    breakdownTotalPages: number;
    handleDriverSideBreakdownClick: (anomalyType: string) => Promise<void>;
    handleBreakdownPageChange: (page: number) => void;
    handleBreakdownPageSizeChange: (size: number) => void;
    handleBreakdownRowClick: (anomalyType: string, dspId: number, dspName: string, driverId?: number, driverName?: string) => void;
    loading: boolean;
    error: any;
    pagination: {
        current: number;
        pageSize: number;
        total: number;
    } | null;
    tablePagination?: {
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
        hasNext: boolean;
        hasPrevious: boolean;
    };
    handlePageChange: (page: number, pageSize?: number) => void;
    handleSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    handleDetailSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => Promise<void>;
    // 详情分页（受控，后端分页）
    detailPagination: { page: number; size: number; total: number };
    handleDetailPageChange: (page: number, pageSize?: number) => Promise<void>;
    detailSortConfig: { sortBy: string; sortDirection: 'ASC' | 'DESC' };
    detailLoading: boolean;
    orderDetailVisible: boolean;
    orderId: string;
    setOrderDetailVisible: (visible: boolean) => void;
    showOrderDetail: (orderId: number) => void;
    warehouseId: string;
    lastDetailContext: { type: string; record: SlaTableRow; statusField?: string } | null;
    dspMode: boolean;
    currentDspId: string | null;
    dataUpdatedAtText: string;
}

// 异常预警类型映射
const ANOMALY_TYPE_MAP: { [key: string]: string } = {
    '超24小时未领取': 'isUnpicked24h',
    '即将超时(24小时)': 'isAboutToTimeout24h',
    '即将超时24小时': 'isAboutToTimeout24h',
    '领取超24小时未妥投': 'isUndelivered24h',
    '即将超时(48小时)': 'isAboutToTimeout48h',
    '即将超时48小时': 'isAboutToTimeout48h',
    '领取超48小时未妥投': 'isUndelivered48h',
    '领取超72小时未妥投': 'isUndelivered72h',
    '即将超时(72小时)': 'isAboutToTimeout72h',
    '即将超时72小时': 'isAboutToTimeout72h',
    '失败未返仓': 'isFailedIncomplete',
    '已断更>24h': 'isNoUpdate24h',
    '已断更24h': 'isNoUpdate24h',
    '已断更>72h': 'isNoUpdate72h'
};

// 正常类型映射
const NORMAL_TYPE_MAP: { [key: string]: string } = {
    '实际到件': 'actual_arrivals',
    '已到货': 'actual_arrivals',
    '派送中': 'in_delivery',
    '已妥投': 'delivered_latest',
    '已妥投(最新)': 'delivered_latest',
    '已妥投（最新）': 'delivered_latest',
    '已退件/破损/丢失': 'returned_damaged_lost',
    '失败/未完结': 'failed_incomplete',
    '已领取': 'picked',
    '未领取': 'unpicked',
    '派件失败已返仓': 'failed_returned_to_whs',
    '派件失败未返仓': 'failed_incomplete',
    '退件': 'returned_parcel',
    '丢失/破损': 'lost_damaged',
    '退件开始后72小时内未退件完成': 'return_incomplete_72h',
    '有过返仓扫描且返仓后24小时内无司机领取或退件开始': 'return_warehouse_24h_unprocessed',
    'Incomplete within 72h after return start': 'return_incomplete_72h',
    'Return warehouse scan with no pickup or return start within 24h': 'return_warehouse_24h_unprocessed',
    'Incompleto en 72 h tras inicio de devolución': 'return_incomplete_72h',
    'Escaneo de retorno a almacén sin recogida ni inicio de devolución en 24 h': 'return_warehouse_24h_unprocessed'
};

export type SlaMonitoringPageMode = 'dsp' | 'whs';

export const useSlaMonitoring = (mode: SlaMonitoringPageMode): UseSlaMonitoringResult => {
    const { t } = useTranslation();
    const location = useLocation();
    const client = useApolloClient();
    const { dspId, dsp: currentDsp } = useCurrentDsp();
    const isDsp = mode === 'dsp';

    const defaultDateRange = getDateRangeByPreset('last3days');
    const today = dayjs().startOf('day');
    // 支持深链 ?arrivalPointType=1 直达「到达HUB时间」视图；不传/非法值回落「到达WHS时间」
    const defaultArrivalPointType = parseArrivalPointTypeFromSearch(location.search);
    const [filters, setFilters] = useState<SlaMonitoringFilters>({
        dateRangePreset: 'last3days',
        dateRange: defaultDateRange,
        arrivalDate: defaultDateRange[1],
        selectedCities: [],
        selectedDsps: [],
        selectedAreas: [],
        selectedDrivers: [],
        arrivalPointType: defaultArrivalPointType,
        arrivalTimeDateRange: [today, today],
        arrivalTimeDatePreset: 'today',
        viewMode: DIMENSION_AREA
    });
    
    // 分页状态
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10); // 一页10条
    
    // 排序状态 - 用于保持翻页时的排序配置
    const [sortConfig, setSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'overallConversionRate', // 默认按整体转化率排序
        sortDirection: 'DESC' // 默认从高到低
    });
    
    // 抽屉相关状态
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerTitle, setDrawerTitle] = useState('');
    const [drawerData, setDrawerData] = useState<DetailData[]>([]);
    const [drawerColumns, setDrawerColumns] = useState<any[]>([]);
    // 详情分页与上下文
    const [detailPage, setDetailPage] = useState(1);
    const [detailPageSize, setDetailPageSize] = useState(10);
    const [detailTotal, setDetailTotal] = useState(0);
    const [lastDetailContext, setLastDetailContext] = useState<{ type: string; record: SlaTableRow; statusField?: string } | null>(null);
    
    // 详情排序状态 - 默认按状态排序（降序，DELIVERY_FAILED排在前面）
    const [detailSortConfig, setDetailSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'overallStatus',
        sortDirection: 'DESC'
    });
    
    // 订单详情相关状态
    const [orderDetailVisible, setOrderDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');

    // 断更司机侧二级页（DSP/司机数量 breakdown）
    const [breakdownDrawerVisible, setBreakdownDrawerVisible] = useState(false);
    const [breakdownData, setBreakdownData] = useState<DspDriverBreakdownItemType[]>([]);
    const [breakdownTitle, setBreakdownTitle] = useState('');
    const [breakdownAnomalyType, setBreakdownAnomalyType] = useState('');
    const [breakdownLoading, setBreakdownLoading] = useState(false);
    const [breakdownPage, setBreakdownPage] = useState(1);
    const [breakdownPageSize, setBreakdownPageSize] = useState(10);
    const [breakdownTotal, setBreakdownTotal] = useState(0);
    const [breakdownTotalPages, setBreakdownTotalPages] = useState(0);
    
    // 详情loading状态
    const [detailLoading, setDetailLoading] = useState(false);
    
    // 详情筛选状态
    const [detailFilters, setDetailFilters] = useState<{
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
        selectedStatuses: string[];
        undeliveredReasonLevel1?: UndeliveredLevel1;
        undeliveredReasonLevel2?: UndeliveredLevel2;
    }>({
        selectedDsps: [],
        selectedAreas: [],
        selectedDrivers: [],
        selectedStatuses: []
    });
    
    // 详情筛选选项（从详情查询响应中获取）
    const [detailFilterOptions, setDetailFilterOptions] = useState<{
        dsps: Array<{ id: number; name: string }>;
        areas: Array<{ id: number; name: string }>;
        drivers: Array<{ id: number; name: string }>;
    } | null>(null); // null 表示还未从详情查询中获取，非 null 表示已获取（即使为空数组）

    // 使用GraphQL查询（区域/司机统一接口，通过 filter.dimension 区分）
    const {
        summaryData,
        recordData,
        loading,
        error,
        handleFilter,
        pagination: apiPagination
    } = useDeliveryProgressMonitoringOfSlaQuery(mode);

    const { 
        detailData, 
        pagination, 
        handlePageRequest 
    } = useDeliveryProgressMonitoringOfSlaDetailQuery(mode);

    // 转换API数据为页面所需格式（表格行率由后端计算，用于排序、翻页）
    const areaData: AreaSlaData[] = filters.viewMode === DIMENSION_AREA
        ? recordData.map(record => ({
            id: record.id || '',
            area: record.areaName || `Area-${record.areaId ?? ''}`,
            areaId: record.areaId ?? 0,
            dsp: record.dspName || `DSP-${record.dspId}`,
            dspId: record.dspId,
            arrivalPointType: record.arrivalPointType ?? 0,
            city: record.cityName ?? '',
            actualArrivals: record.actualArrivals,
            pickedCount: record.pickedCount || 0,
            unpickedCount: record.unpickedCount || 0,
            inProgress: record.inProgress,
            deliveredLatest: record.deliveredLatest,
            failedIncomplete: record.failedIncomplete,
            latestDeliveryRate: parseFloat(record.latestDeliveryRate ?? '0'),
            failedReturnedToWhsCount: record.failedReturnedToWhsCount || 0,
            lostDamagedCount: record.lostDamagedCount ?? 0,
            returnedParcelCount: record.returnedParcelCount || 0,
            returnedParcelIncompleteOver72hRate: parseFloat(record.returnedParcelIncompleteOver72hRate ?? '0'),
            failedReturnedToWhsUnprocessedOver24hRate: parseFloat(record.failedReturnedToWhsUnprocessedOver24hRate ?? '0'),
            t1DeliveryRate: parseFloat(record.t1DeliveryRate ?? '0'),
            t2DeliveryRate: parseFloat(record.t2DeliveryRate ?? '0'),
            t3DeliveryRate: parseFloat(record.t3DeliveryRate ?? '0'),
            t4DeliveryRate: parseFloat(record.t4DeliveryRate ?? '0'),
            t5DeliveryRate: parseFloat(record.t5DeliveryRate ?? '0'),
            driverSideLcCount: Number(record.driverSideLcCount ?? 0),
            warehouseSideLcCount: Number(record.warehouseSideLcCount ?? 0),
            handoverDiffLcCount: Number(record.handoverDiffLcCount ?? 0),
            returnLcCount: Number(record.returnLcCount ?? 0),
            outForDeliveryNodeLcCount: Number(record.outForDeliveryNodeLcCount ?? 0),
            failedNotReturnedNodeLcCount: Number(record.failedNotReturnedNodeLcCount ?? 0),
            lostDamagedDriverLcCount: Number(record.lostDamagedDriverLcCount ?? 0),
            returnWrongUnscanNodeLcCount: Number(record.returnWrongUnscanNodeLcCount ?? 0),
            notPickedUpLcCount: Number(record.notPickedUpLcCount ?? 0),
            returnNotPutAwayLcCount: Number(record.returnNotPutAwayLcCount ?? 0),
            returnOnPutAwayLcCount: Number(record.returnOnPutAwayLcCount ?? 0),
            returnUnPutAwayLcCount: Number(record.returnUnPutAwayLcCount ?? 0),
            lostDamagedWhsLcCount: Number(record.lostDamagedWhsLcCount ?? 0),
            otherLcCount: Number(record.otherLcCount ?? 0),
            handoverDiffNodeLcCount: Number(record.handoverDiffNodeLcCount ?? 0),
            returnPkgLcCount: Number(record.returnPkgLcCount ?? 0),
            delivered36hCount: Number(record.delivered36hCount ?? 0),
        }))
        : [];

    const driverData: DriverSlaData[] = filters.viewMode === DIMENSION_DRIVER
        ? recordData.map(record => ({
            id: record.id || '',
            driverId: record.driverId ?? 0,
            driverName: record.driverName ?? '',
            dsp: record.dspName || `DSP-${record.dspId}`,
            dspId: record.dspId,
            arrivalPointType: record.arrivalPointType ?? 0,
            actualArrivals: record.actualArrivals,
            pickedCount: record.pickedCount || 0,
            unpickedCount: record.unpickedCount || 0,
            inProgress: record.inProgress,
            deliveredLatest: record.deliveredLatest,
            failedIncomplete: record.failedIncomplete,
            latestDeliveryRate: parseFloat(record.latestDeliveryRate ?? '0'),
            failedReturnedToWhsCount: record.failedReturnedToWhsCount || 0,
            lostDamagedCount: record.lostDamagedCount ?? 0,
            returnedParcelCount: record.returnedParcelCount || 0,
            returnedParcelIncompleteOver72hRate: parseFloat(record.returnedParcelIncompleteOver72hRate ?? '0'),
            failedReturnedToWhsUnprocessedOver24hRate: parseFloat(record.failedReturnedToWhsUnprocessedOver24hRate ?? '0'),
            t1DeliveryRate: parseFloat(record.t1DeliveryRate ?? '0'),
            t2DeliveryRate: parseFloat(record.t2DeliveryRate ?? '0'),
            t3DeliveryRate: parseFloat(record.t3DeliveryRate ?? '0'),
            t4DeliveryRate: parseFloat(record.t4DeliveryRate ?? '0'),
            t5DeliveryRate: parseFloat(record.t5DeliveryRate ?? '0'),
            driverSideLcCount: Number(record.driverSideLcCount ?? 0),
            warehouseSideLcCount: Number(record.warehouseSideLcCount ?? 0),
            handoverDiffLcCount: Number(record.handoverDiffLcCount ?? 0),
            returnLcCount: Number(record.returnLcCount ?? 0),
            outForDeliveryNodeLcCount: Number(record.outForDeliveryNodeLcCount ?? 0),
            failedNotReturnedNodeLcCount: Number(record.failedNotReturnedNodeLcCount ?? 0),
            lostDamagedDriverLcCount: Number(record.lostDamagedDriverLcCount ?? 0),
            returnWrongUnscanNodeLcCount: Number(record.returnWrongUnscanNodeLcCount ?? 0),
            notPickedUpLcCount: Number(record.notPickedUpLcCount ?? 0),
            returnNotPutAwayLcCount: Number(record.returnNotPutAwayLcCount ?? 0),
            returnOnPutAwayLcCount: Number(record.returnOnPutAwayLcCount ?? 0),
            returnUnPutAwayLcCount: Number(record.returnUnPutAwayLcCount ?? 0),
            lostDamagedWhsLcCount: Number(record.lostDamagedWhsLcCount ?? 0),
            otherLcCount: Number(record.otherLcCount ?? 0),
            handoverDiffNodeLcCount: Number(record.handoverDiffNodeLcCount ?? 0),
            returnPkgLcCount: Number(record.returnPkgLcCount ?? 0),
            delivered36hCount: Number(record.delivered36hCount ?? 0),
        }))
        : [];

    // 转换异常数据（已移除，不再使用 anomalySummary）
    const anomalyData: Array<{ type: string; label: string; count: number; color: string }> = [];

    // 获取当前用户仓库（用于主列表与 FilterOptions）
    const { currentWarehouse } = useWarehouseContext();
    // 仓库来自树节点选中的 currentWarehouse；未选时为空，走「请选择仓库」空态
    const warehouseId = currentWarehouse?.id ? String(currentWarehouse.id) : '';

    // 与 getDeliveryProgressMonitoringOfSlaPageList 的 filter 同结构；首轮无 dsps/areas/drivers 时 dspIds/areaIds/driverIds 为 undefined
    const filterForOptions = useMemo(() => {
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        return {
            arrivalDateStart,
            arrivalDateEnd,
            warehouseIds: warehouseId ? [Number(warehouseId)] : undefined,
            dspIds: undefined as number[] | undefined,
            areaIds: undefined as number[] | undefined,
            cityNames: undefined as string[] | undefined,
            driverIds: undefined as number[] | undefined,
            arrivalPointType: filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS,
            dimension: filters.viewMode,
        };
    }, [
        filters.arrivalDate,
        filters.arrivalTimeDateRange,
        filters.arrivalPointType,
        filters.viewMode,
        warehouseId,
    ]);

    const listFilterRef = useRef<DeliveryProgressMonitoringOfSlaFiltersType | undefined>(undefined);

    // 下游多处用「名称」反查 id 提交筛选。搜索结果会随关键词变化，
    // 若只依赖当前结果，已选中的项在换词后就查不到 id、筛选会静默失效，
    // 因此累积缓存所有见过的 名称→id 映射。
    const dspIdByName = useRef<Map<string, number>>(new Map());
    const areaIdByName = useRef<Map<string, number>>(new Map());
    // 司机下拉的 value 是 accountId，反向缓存用于已选项回显与抽屉标题显示姓名
    const driverNameById = useRef<Map<number, string>>(new Map());

    // DSP/区域/司机选项均改为按关键词查主库（原先由 FilterOptions 聚合 SLA 大表约 9.6s）。
    // 关键词为空时也请求一次，用于下拉初次展开时给出前 N 条候选。
    const [dspKeyword, setDspKeyword] = useState('');
    const [areaKeyword, setAreaKeyword] = useState('');
    const [driverKeyword, setDriverKeyword] = useState('');
    const numericWarehouseIds = useMemo(
        () => (warehouseId ? [Number(warehouseId)] : undefined),
        [warehouseId]
    );

    // DSP 用户的 DSP 下拉在页面级是隐藏的、抽屉里是禁用的，不需要同仓其它 DSP 的名单
    const { data: dspOptionsData, loading: dspOptionsLoading } = useQuery<
        SearchDeliveryFilterDspOptionsResponse
    >(SEARCH_DELIVERY_FILTER_DSP_OPTIONS, {
        variables: { warehouseIds: numericWarehouseIds, keyword: dspKeyword || null },
        skip: !warehouseId || isDsp
    });

    // DSP 用户的唯一选项就是自己：让两处「自动设置默认DSP」的 effect 仍能命中，
    // 从而抽屉里那个禁用的 DSP 框显示自身名称，且 dspIdByName 能解析该名称
    const ownDspOptions = useMemo(
        () => (currentDsp?.id != null && currentDsp?.name
            ? [{ id: Number(currentDsp.id), name: currentDsp.name }]
            : undefined),
        [currentDsp?.id, currentDsp?.name]
    );

    const { data: areaOptionsData, loading: areaOptionsLoading } = useQuery<
        SearchDeliveryFilterAreaOptionsResponse | SearchDeliveryFilterAreaOptionsForDspResponse
    >(isDsp ? SEARCH_DELIVERY_FILTER_AREA_OPTIONS_FOR_DSP : SEARCH_DELIVERY_FILTER_AREA_OPTIONS, {
        variables: { warehouseIds: numericWarehouseIds, keyword: areaKeyword || null },
        skip: !warehouseId || filters.viewMode !== DIMENSION_AREA
    });

    // 主筛选 DSP→司机级联，与抽屉的 drawerCascadedDspIds 口径一致。
    // 不传 dspIds 时后端会按仓库反查该仓关联的全部 DSP（searchDriverOptions:171），
    // 下拉会列出其它 DSP 的司机；DSP 视角还构成越权展示。
    const mainCascadedDspIds = useMemo(() => {
        if (isDsp) return dspId ? [Number(dspId)] : undefined;
        const ids = filters.selectedDsps
            .map((name) => dspIdByName.current.get(name))
            .filter((id): id is number => id != null)
            .map(Number);
        return ids.length > 0 ? ids : undefined;
    }, [isDsp, dspId, filters.selectedDsps]);

    const { data: driverOptionsData, loading: driverOptionsLoading } = useQuery<
        SearchDeliveryFilterDriverOptionsResponse | SearchDeliveryFilterDriverOptionsForDspResponse
    >(isDsp ? SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS_FOR_DSP : SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS, {
        variables: {
            warehouseIds: numericWarehouseIds,
            dspIds: mainCascadedDspIds,
            keyword: driverKeyword || null
        },
        skip: !warehouseId || filters.viewMode !== DIMENSION_DRIVER
    });

    const searchedDsps = isDsp
        ? ownDspOptions
        : dspOptionsData?.searchDeliveryFilterDspOptions;
    const searchedAreas = isDsp
        ? (areaOptionsData as SearchDeliveryFilterAreaOptionsForDspResponse | undefined)?.searchDeliveryFilterAreaOptionsForDsp
        : (areaOptionsData as SearchDeliveryFilterAreaOptionsResponse | undefined)?.searchDeliveryFilterAreaOptions;
    const searchedDrivers = isDsp
        ? (driverOptionsData as SearchDeliveryFilterDriverOptionsForDspResponse | undefined)?.searchDeliveryFilterDriverOptionsForDsp
        : (driverOptionsData as SearchDeliveryFilterDriverOptionsResponse | undefined)?.searchDeliveryFilterDriverOptions;

    (searchedDsps ?? []).forEach((option) => dspIdByName.current.set(option.name, option.id));
    (searchedAreas ?? []).forEach((option) => areaIdByName.current.set(option.name, option.id));
    rememberDriverNames(searchedDrivers, driverNameById.current);

    /** 搜索结果并入「已选项」，否则 antd Select 无法回显已选标签 */
    const mergeSelectedIntoOptions = (
        searched: DeliveryFilterOption[] | undefined,
        selectedNames: string[] | undefined,
        idByName: Map<string, number>
    ): DeliveryFilterOption[] => {
        const merged = [...(searched ?? [])];
        (selectedNames ?? []).forEach((name) => {
            if (!name || merged.some((option) => option.name === name)) return;
            merged.push({ id: idByName.get(name) ?? -1, name });
        });
        return merged;
    };

    /** 行下钻/二级页会把行上的名称写进抽屉筛选，这些名称通常不在搜索结果里，故先把行上的 id 记入缓存 */
    const rememberRowOptionIds = (record: SlaTableRow) => {
        if ('dsp' in record && record.dsp && record.dspId) dspIdByName.current.set(record.dsp, Number(record.dspId));
        if ('area' in record && record.area && record.areaId) areaIdByName.current.set(record.area, Number(record.areaId));
        if ('driverName' in record && record.driverName && record.driverId) {
            driverNameById.current.set(Number(record.driverId), record.driverName);
        }
    };

    // 抽屉内的已选项由行下钻写入，未必命中按关键词搜索的选项，需一并并入，否则下游「名称→id」反查不到会丢掉筛选条件
    const mainListDsps = useMemo(
        () => mergeSelectedIntoOptions(searchedDsps, [...filters.selectedDsps, ...detailFilters.selectedDsps], dspIdByName.current),
        [searchedDsps, filters.selectedDsps, detailFilters.selectedDsps]
    );
    const mainListAreas = useMemo(
        () => mergeSelectedIntoOptions(searchedAreas, [...filters.selectedAreas, ...detailFilters.selectedAreas], areaIdByName.current),
        [searchedAreas, filters.selectedAreas, detailFilters.selectedAreas]
    );
    const mainListDrivers = useMemo(
        () => buildDriverOptions(searchedDrivers, [...filters.selectedDrivers, ...detailFilters.selectedDrivers], driverNameById.current),
        [searchedDrivers, filters.selectedDrivers, detailFilters.selectedDrivers]
    );

    // 主列表筛选始终用 FilterOptions 接口数据，避免被详情接口的 detailFilterOptions 覆盖导致主列表下拉变空
    const dsps = mainListDsps;
    const areas = mainListAreas;
    const drivers = mainListDrivers;
    // 详情抽屉：默认用“全量选项”（主列表 filterOptions），避免详情接口返回的子集把下拉选项变窄
    const detailDrawerDsps = mainListDsps;
    const detailDrawerAreas = mainListAreas;
    const detailDrawerDrivers = mainListDrivers;

    // 详情抽屉内 DSP-司机级联：
    // - `detailDrawerDrivers` 永远是全量司机（不随抽屉内筛选变化）
    // - `drawerDrivers` 会在抽屉内选中 DSP 时，通过 options 接口拿到“该 DSP 下的司机子集”
    const drawerCascadedDspIds = useMemo(() => {
        if (!detailFilters.selectedDsps || detailFilters.selectedDsps.length === 0) return null;
        const ids = detailFilters.selectedDsps
            .map((name) => detailDrawerDsps.find((d: { id: number; name: string }) => d.name === name)?.id)
            .filter((id): id is number => id != null);
        return ids.length > 0 ? ids : null;
    }, [detailFilters.selectedDsps, detailDrawerDsps]);

    const drawerCascadedFilterForOptions = useMemo(() => {
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        // 详情抽屉 DSP→司机级联：固定使用 'driver' 维度，确保后端根据 dspIds 过滤司机选项
        //（在区域维度下如果仍传 'area'，后端可能不会返回 drivers 或返回空数组）
        return {
            arrivalDateStart,
            arrivalDateEnd,
            warehouseIds: warehouseId ? [Number(warehouseId)] : undefined,
            dspIds: drawerCascadedDspIds ?? undefined,
            areaIds: undefined as number[] | undefined,
            cityNames: undefined as string[] | undefined,
            driverIds: undefined as number[] | undefined,
            arrivalPointType: filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS,
            dimension: DIMENSION_DRIVER,
        };
    }, [
        drawerCascadedDspIds,
        filters.arrivalDate,
        filters.arrivalTimeDateRange,
        filters.arrivalPointType,
        warehouseId,
    ]);

    // 抽屉内 DSP→司机级联同样走主库按需搜索，与主列表口径一致
    const [drawerDriverKeyword, setDrawerDriverKeyword] = useState('');
    const { data: drawerCascadedDriverData, loading: drawerDriverOptionsLoading } = useQuery<
        SearchDeliveryFilterDriverOptionsResponse | SearchDeliveryFilterDriverOptionsForDspResponse
    >(isDsp ? SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS_FOR_DSP : SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS, {
        variables: {
            warehouseIds: numericWarehouseIds,
            dspIds: drawerCascadedDspIds ?? undefined,
            keyword: drawerDriverKeyword || null
        },
        skip: !warehouseId || !drawerVisible
    });

    const drawerDrivers = useMemo(() => {
        const searched = isDsp
            ? (drawerCascadedDriverData as SearchDeliveryFilterDriverOptionsForDspResponse | undefined)?.searchDeliveryFilterDriverOptionsForDsp
            : (drawerCascadedDriverData as SearchDeliveryFilterDriverOptionsResponse | undefined)?.searchDeliveryFilterDriverOptions;
        rememberDriverNames(searched, driverNameById.current);
        if (!searched) return detailDrawerDrivers;
        return buildDriverOptions(searched, detailFilters.selectedDrivers, driverNameById.current);
    }, [drawerCascadedDriverData, detailDrawerDrivers, detailFilters.selectedDrivers, isDsp]);

    // 约束：司机选择必须来自当前下拉选项，否则筛选条件与下拉展示不一致。
    // 因此当 DSP 改变后，如果“已选司机”不在 `drawerDrivers` 中，自动清理无效项；「未分配」哨兵不受 DSP 收窄影响。
    useEffect(() => {
        if (!drawerVisible || !drawerCascadedDspIds) return;
        if (!detailFilters.selectedDrivers || detailFilters.selectedDrivers.length === 0) return;
        const allowed = new Set(drawerDrivers.map((d) => String(d.accountId)));
        const nextSelectedDrivers = detailFilters.selectedDrivers.filter((value) => value === String(UNASSIGNED_DRIVER_ID) || allowed.has(value));
        if (nextSelectedDrivers.length === detailFilters.selectedDrivers.length) return;
        setDetailFilters({
            ...detailFilters,
            selectedDrivers: nextSelectedDrivers,
        });
    }, [drawerVisible, drawerCascadedDspIds, drawerDrivers, detailFilters]);

    // 与 getDeliveryProgressMonitoringOfSlaPageList 的 filter 完全一致（仅无分页），供主列表与后续逻辑使用
    const listFilter = useMemo(() => {
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const selectedDspIdsFromFilter = filters.selectedDsps.length > 0
            ? filters.selectedDsps.map((dspName: string) => {
                const dsp = dsps.find((d: { name: string; id: number }) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter((id: number | null) => id !== null) as number[]
            : undefined;
        const finalDspIds = isDsp && dspId ? [parseInt(dspId)] : selectedDspIdsFromFilter;
        const selectedAreaIds = filters.viewMode === DIMENSION_AREA && filters.selectedAreas.length > 0
            ? filters.selectedAreas.map((areaName: string) => {
                const area = areas.find((a: { name: string; id: number }) => a.name === areaName);
                return area ? area.id : null;
            }).filter((id: number | null) => id !== null) as number[]
            : undefined;
        const selectedDriverIds = toDriverIds(filters.selectedDrivers);
        return {
            arrivalDateStart,
            arrivalDateEnd,
            warehouseIds: warehouseId ? [Number(warehouseId)] : undefined,
            dspIds: finalDspIds,
            areaIds: filters.viewMode === DIMENSION_AREA ? selectedAreaIds : undefined,
            cityNames: undefined as string[] | undefined,
            driverIds: filters.viewMode === DIMENSION_DRIVER ? selectedDriverIds : undefined,
            arrivalPointType: filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS,
            dimension: filters.viewMode,
        };
    }, [
        filters.arrivalDate,
        filters.arrivalTimeDateRange,
        filters.arrivalPointType,
        filters.viewMode,
        filters.selectedDsps,
        filters.selectedAreas,
        filters.selectedDrivers,
        warehouseId,
        dsps,
        areas,
        drivers,
        isDsp,
        dspId,
    ]);
    listFilterRef.current = listFilter;

    // 当DSP经理登录且DSP选项加载完成时，自动设置默认DSP（汇总页面）
    useEffect(() => {
        if (isDsp && currentDsp && currentDsp.name && mainListDsps.length > 0) {
            // 检查当前DSP是否在选项中
            const currentDspInOptions = mainListDsps.find((d: any) => d.id === currentDsp.id || d.name === currentDsp.name);
            if (currentDspInOptions && filters.selectedDsps.length === 0) {
                setFilters(prev => ({
                    ...prev,
                    selectedDsps: [currentDsp.name]
                }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDsp, currentDsp, mainListDsps.length]);

    // 当DSP经理登录且详情DSP选项加载完成时，自动设置默认DSP（详情页面）
    useEffect(() => {
        if (isDsp && currentDsp && currentDsp.name && detailFilters.selectedDsps.length === 0) {
            // 优先使用详情选项，如果没有则使用主列表选项
            const availableDsps = detailFilterOptions?.dsps && detailFilterOptions.dsps.length > 0 
                ? detailFilterOptions.dsps 
                : mainListDsps;
            
            if (availableDsps.length > 0) {
                // 检查当前DSP是否在选项中
                const currentDspInOptions = availableDsps.find((d: any) => d.id === currentDsp.id || d.name === currentDsp.name);
                if (currentDspInOptions) {
                    setDetailFilters(prev => ({
                        ...prev,
                        selectedDsps: [currentDsp.name]
                    }));
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDsp, currentDsp, detailFilterOptions, mainListDsps.length]);

    // 获取详情数据
    const fetchDetailData = async (
        type: string,
        record: AreaSlaData | DriverSlaData,
        sortBy?: string,
        sortDirection?: 'ASC' | 'DESC',
        page?: number,
        size?: number,
        overrideFilters?: {
            selectedDsps?: string[];
            selectedAreas?: string[];
            selectedDrivers?: string[];
            selectedStatuses?: string[];
            undeliveredReasonLevel1?: UndeliveredLevel1;
            undeliveredReasonLevel2?: UndeliveredLevel2;
        }
    ): Promise<{ items: DetailData[]; total: number }> => {
        const currentSelectedStatuses = overrideFilters?.selectedStatuses ?? detailFilters.selectedStatuses;
        // 原因不能用 ??：传了 overrideFilters 即为本次完整的初始筛选，不带原因也要盖掉 state 里上一次残留的值
        const currentUndeliveredReasonLevel1 = overrideFilters ? overrideFilters.undeliveredReasonLevel1 : detailFilters.undeliveredReasonLevel1;
        const currentUndeliveredReasonLevel2 = overrideFilters ? overrideFilters.undeliveredReasonLevel2 : detailFilters.undeliveredReasonLevel2;
        let statusField: string | undefined;
        let statusFields: string[] | undefined;

        if (currentSelectedStatuses.length === 1) {
            statusField = currentSelectedStatuses[0];
        } else if (currentSelectedStatuses.length > 1) {
            statusFields = currentSelectedStatuses;
        }
        // 清空状态时不传 statusField/statusFields，后端不加重状态条件，返回所有状态的数据（与 hub-parcel-sla-monitoring 一致）

        // 优先使用传入的筛选条件，如果没有则使用状态中的筛选条件，最后回退到record的ID
        const currentSelectedDsps = overrideFilters?.selectedDsps ?? detailFilters.selectedDsps;
        const currentSelectedAreas = overrideFilters?.selectedAreas ?? detailFilters.selectedAreas;
        const currentSelectedDrivers = overrideFilters?.selectedDrivers ?? detailFilters.selectedDrivers;

        // 名称→id 一律走累积缓存：选项数组是按关键词搜索的有限结果，且行下钻当帧还没并入已选项
        let selectedDspIds: number[] | undefined;
        if (currentSelectedDsps.length > 0) {
            selectedDspIds = currentSelectedDsps
                .map(dspName => dspIdByName.current.get(dspName) ?? null)
                .filter(id => id !== null) as number[];
            // 如果通过名称查找失败（结果为空数组），回退到使用record的dspId
            if (selectedDspIds.length === 0 && record && record.dspId) {
                selectedDspIds = [record.dspId];
            }
        } else {
            // 如果是DSP经理，自动添加DSP过滤条件
            if (isDsp && dspId) {
                selectedDspIds = [parseInt(dspId)];
            } else if (record && record.dspId) {
                // 如果详情筛选条件为空，回退到使用record的dspId
                selectedDspIds = [record.dspId];
            }
        }

        let selectedAreaIds: number[] | undefined;
        if (currentSelectedAreas.length > 0) {
            selectedAreaIds = currentSelectedAreas
                .map(areaName => areaIdByName.current.get(areaName) ?? null)
                .filter((id): id is number => id !== null);
            if (selectedAreaIds.length === 0 && record && 'areaId' in record && record.areaId) {
                selectedAreaIds = [record.areaId];
            }
        } else if (currentSelectedDrivers == null || currentSelectedDrivers.length === 0) {
            if (record && 'areaId' in record && record.areaId) {
                selectedAreaIds = [record.areaId];
            }
        }

        // 详情筛选条件：司机ID；点击「司机为 -」时仅查未分配司机(driverIdNullOnly)
        const isDriverUnassignedOnly = isUnassignedDriverOnly(currentSelectedDrivers);
        let selectedDriverIds: number[] | undefined;
        let driverIdNullOnly: boolean | undefined;
        if (isDriverUnassignedOnly) {
            driverIdNullOnly = true;
            selectedDriverIds = undefined;
        } else {
            selectedDriverIds = toDriverIds(currentSelectedDrivers);
        }

        // 一级页面选中的城市，传给详情接口
        const selectedCityNames = filters.selectedCities?.length ? filters.selectedCities : undefined;

        // 构建分页请求（单选用 statusField，多选用 statusFields）；同时带上一级页的 whs、日期、地区等
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const detailArrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const detailArrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType = {
            filter: {
                arrivalDateStart: detailArrivalDateStart,
                arrivalDateEnd: detailArrivalDateEnd,
                warehouseIds: warehouseId ? [Number(warehouseId)] : [],
                dspIds: selectedDspIds,
                areaIds: selectedAreaIds,
                driverIds: selectedDriverIds,
                driverIdNullOnly,
                cityNames: selectedCityNames,
                trackingNumber: undefined,
                statusField: statusField,
                statusFields: statusFields,
                arrivalPointType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS),
                undeliveredReasonLevel1: currentUndeliveredReasonLevel1,
                undeliveredReasonLevel2: currentUndeliveredReasonLevel2,
            },
            page: (page ?? detailPage) - 1,
            size: size ?? detailPageSize,
            sortBy: sortBy || detailSortConfig.sortBy,
            sortDirection: sortDirection || detailSortConfig.sortDirection
        };

        try {
            // 直接调用详情查询API
            const { data: detailResponse } = await client.query({
                query: isDsp ? GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST : GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
                variables: { pageRequest }
            });

            const detailResp = isDsp
                ? detailResponse?.getDeliveryProgressMonitoringOfSlaDetailListForDsp
                : detailResponse?.getDeliveryProgressMonitoringOfSlaDetailList;

            if (!detailResp) {
                console.error('detailResp 为空');
                return { items: [], total: 0 };
            }
            
            const detailItems = detailResp?.content || [];
            
            // 转换数据格式
            const convertedData: DetailData[] = detailItems.map((item: any) => ({
                id: item.id,
                dsp_name: (item.lastOperateDspName ?? item.lastAssignDspName) || '',
                area_name: item.areaName || '',
                driver_id: item.lastOperateDriverId ?? item.lastAssignDriverId ?? 0,
                driver_name: (item.lastOperateDriverName ?? item.lastAssignDriverName) || '-',
                last_operate_dsp_name: item.lastOperateDspName || '',
                last_operate_driver_name: item.lastOperateDriverName || '',
                last_assign_dsp_name: item.lastAssignDspName || '',
                last_assign_driver_name: item.lastAssignDriverName || '',
                task_date: item.taskDate || '',
                tracking_number: item.trackingNumber,
                status: item.overallStatusText || item.overallStatus?.toString() || '',
                undelivered_reason_level_1: item.undeliveredReasonLevel1 || undefined,
                undelivered_reason_level_2: item.undeliveredReasonLevel2 || undefined,
                order_id: item.orderId
            }));

            const totalElements: number = detailResp?.totalElements ?? detailItems.length;
            
            return { items: convertedData, total: totalElements };
        } catch (error) {
            console.error('fetchDetailData 获取详情数据失败:', error);
            return { items: [], total: 0 };
        }
    };


    // 根据类型获取列定义
    // 显示订单详情
    const showOrderDetail = (orderId: number) => {
        if (orderId) {
            setOrderId(orderId.toString());
            setOrderDetailVisible(true);
        }
    };

    const getColumnsByType = (type: string, onSortChange?: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void) => {
        // 排序图标渲染函数
        const renderSortIcon = (field: string) => {
            const isActive = detailSortConfig.sortBy === field;
            const isDesc = detailSortConfig.sortDirection === 'DESC';
            
            if (isActive) {
                return isDesc 
                    ? React.createElement(SortDescendingOutlined, { className: "ml-1 cursor-pointer text-blue-500" })
                    : React.createElement(SortAscendingOutlined, { className: "ml-1 cursor-pointer text-blue-500" });
            } else {
                return React.createElement(SortDescendingOutlined, { className: "ml-1 cursor-pointer text-gray-400" });
            }
        };

        const baseColumns = [
            { 
                title: React.createElement('span', null,
                    t('wms.delivery.parcelSlaMonitoring.detailColumns.area'),
                    onSortChange && React.createElement('span', {
                        onClick: () => {
                            const newDirection = detailSortConfig.sortBy === 'areaId' && detailSortConfig.sortDirection === 'ASC' ? 'DESC' : 'ASC';
                            onSortChange('areaId', newDirection);
                        }
                    }, renderSortIcon('areaId'))
                ), 
                dataIndex: 'area_name', 
                key: 'area_name' 
            },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.lastOperateDsp'),
                dataIndex: 'last_operate_dsp_name', 
                key: 'last_operate_dsp_name',
                render: (text: string) => text || '-'
            },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.lastOperateDriver'),
                dataIndex: 'last_operate_driver_name', 
                key: 'last_operate_driver_name',
                render: (text: string) => text || '-'
            },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.lastAssignDsp'),
                dataIndex: 'last_assign_dsp_name', 
                key: 'last_assign_dsp_name',
                render: (text: string) => text || '-'
            },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.lastAssignDriver'),
                dataIndex: 'last_assign_driver_name', 
                key: 'last_assign_driver_name',
                render: (text: string) => text || '-'
            },
            { title: t('wms.delivery.parcelSlaMonitoring.detailColumns.taskDate'), dataIndex: 'task_date', key: 'task_date' },
            { 
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.trackingNumber'), 
                dataIndex: 'tracking_number', 
                key: 'tracking_number',
                render: (text: string, record: any) => React.createElement('a', {
                    href: '#',
                    onClick: (e: React.MouseEvent) => {
                        e.preventDefault();
                        showOrderDetail(record.order_id);
                    },
                    style: { color: '#1890ff' }
                }, text)
            },
            {
                title: React.createElement('span', null,
                    t('wms.delivery.parcelSlaMonitoring.detailColumns.status'),
                    onSortChange && React.createElement('span', {
                        onClick: () => {
                            const newDirection = detailSortConfig.sortBy === 'overallStatus' && detailSortConfig.sortDirection === 'ASC' ? 'DESC' : 'ASC';
                            onSortChange('overallStatus', newDirection);
                        }
                    }, renderSortIcon('overallStatus'))
                ),
                dataIndex: 'status',
                key: 'status'
            },
            {
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.undeliveredReasonLevel1'),
                dataIndex: 'undelivered_reason_level_1',
                key: 'undelivered_reason_level_1',
                render: (value?: string) => {
                    const meta = value ? LEVEL1_META[value as UndeliveredLevel1] : undefined;
                    if (!meta) return '-';
                    return React.createElement('span', {
                        style: {
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 4,
                            color: '#fff',
                            backgroundColor: meta.color,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                        },
                    }, getLevel1Label(t, value as UndeliveredLevel1));
                },
            },
            {
                title: t('wms.delivery.parcelSlaMonitoring.detailColumns.undeliveredReasonLevel2'),
                dataIndex: 'undelivered_reason_level_2',
                key: 'undelivered_reason_level_2',
                render: (value?: string) => {
                    if (!value) return '-';
                    const meta = LEVEL2_META[value as UndeliveredLevel2];
                    return meta ? getLevel2Label(t, value as UndeliveredLevel2) : value;
                },
            }
        ];

        if (type === 'delivered_latest') {
            return [...baseColumns, { title: t('wms.delivery.parcelSlaMonitoring.detailColumns.deliveredTime'), dataIndex: 'delivered_time', key: 'delivered_time' }];
        }

        if (type === 'failed_incomplete') {
            return baseColumns;
        }

        return baseColumns;
    };

    // 点击数字查看详情
    const handleDetailClick = async (type: string, record: SlaTableRow) => {
        rememberRowOptionIds(record);

        const typeMap: { [key: string]: string } = {
            'actual_arrivals': t('wms.delivery.parcelSlaMonitoring.columns.actualArrivals'),
            'in_progress': t('wms.delivery.parcelSlaMonitoring.columns.inProgress'),
            'delivered_latest': t('wms.delivery.parcelSlaMonitoring.columns.deliveredLatest'),
            'failed_incomplete': t('wms.delivery.parcelSlaMonitoring.columns.failedNotReturned'),
            'returned_damaged_lost': t('wms.delivery.parcelSlaMonitoring.columns.returnedDamagedLost'),
            'picked': t('wms.delivery.parcelSlaMonitoring.columns.pickedCount'),
            'unpicked': t('wms.delivery.parcelSlaMonitoring.columns.unpickedCount'),
            'failed_returned_to_whs': t('wms.delivery.parcelSlaMonitoring.columns.failedReturnedToWhsCount'),
            'returned_parcel': t('wms.delivery.parcelSlaMonitoring.columns.returnedParcelCount'),
            'lost_damaged': t('wms.delivery.parcelSlaMonitoring.columns.lostDamaged'),
            'return_incomplete_72h': t('wms.delivery.parcelSlaMonitoring.detailDrawer.returnIncomplete72hDrillTitle'),
            'return_warehouse_24h_unprocessed': t('wms.delivery.parcelSlaMonitoring.detailDrawer.returnWarehouse24hDrillTitle')
        };

        // v2 未妥投归因：'v2:ALL'（未妥投总数）按状态「未妥投」筛；'v2:L1:L2'（子列）预填一级/二级原因
        const v2Decoded = type.startsWith('v2:') && type !== 'v2:ALL' ? decodeUndeliveredAnomaly(type) : null;
        // 标题按 ' - ' 切分推断类型，L1/L2 之间用不带空格的 '-'
        const v2Label = type === 'v2:ALL'
            ? t('wms.delivery.parcelSlaMonitoring.columns.undeliveredTotal')
            : (v2Decoded
                ? (v2Decoded.level2
                    ? `${getLevel1Label(t, v2Decoded.level1)}-${getLevel2LabelInGroup(t, v2Decoded.level2)}`
                    : getLevel1Label(t, v2Decoded.level1))
                : undefined);

        const isDriverRecord = filters.viewMode === DIMENSION_DRIVER;
        const displayName = isDriverRecord
            ? (record as DriverSlaData).driverName
            : (record as AreaSlaData).area;
        setDrawerTitle(`${displayName} - ${v2Label ?? typeMap[type]} - ${t('wms.delivery.parcelSlaMonitoring.detailDrawer.detailPage')}`);

        const columns = getColumnsByType(type, handleDetailSortChange);
        setDrawerColumns(columns);

        let initialDsps: string[] = [];
        if (isDsp && currentDsp && currentDsp.name) {
            initialDsps = [currentDsp.name];
        } else if (isDriverRecord && (record as DriverSlaData).dsp) {
            initialDsps = [(record as DriverSlaData).dsp];
        } else if (!isDriverRecord && (record as AreaSlaData).dsp) {
            initialDsps = [(record as AreaSlaData).dsp];
        }

        // 获取状态字段（用于抽屉默认筛选与 lastDetailContext）
        let statusFieldForContext: string | undefined;
        const anomalyStatusMap: { [key: string]: string } = {
            'unpicked24h': 'isUnpicked24h',
            'undelivered24h': 'isUndelivered24h',
            'undelivered48h': 'isUndelivered48h',
            'undelivered72h': 'isUndelivered72h',
            'aboutToTimeout24h': 'isAboutToTimeout24h',
            'aboutToTimeout48h': 'isAboutToTimeout48h',
            'lostDamaged': 'isLostDamaged',
            'noUpdate24h': 'isNoUpdate24h'
        };
        if (anomalyStatusMap[type]) {
            statusFieldForContext = anomalyStatusMap[type];
        } else {
            switch (type) {
                case 'actual_arrivals': statusFieldForContext = 'isActualArrivals'; break;
                case 'in_progress': case 'in_delivery': statusFieldForContext = 'isInDelivery'; break;
                case 'delivered_latest': statusFieldForContext = 'isDeliveredLatest'; break;
                case 'returned_damaged_lost': statusFieldForContext = 'isReturnedDamagedLost'; break;
                case 'failed_incomplete': statusFieldForContext = 'isFailedIncomplete'; break;
                case 'picked': statusFieldForContext = 'isPicked'; break;
                case 'unpicked': statusFieldForContext = 'isUnpicked'; break;
                case 'failed_returned_to_whs': statusFieldForContext = 'isFailedReturnedToWhs'; break;
                case 'returned_parcel': statusFieldForContext = 'isReturnedParcel'; break;
                case 'lost_damaged': statusFieldForContext = 'isLostDamaged'; break;
                case 'return_incomplete_72h': statusFieldForContext = 'isReturnedParcelIncompleteOver72h'; break;
                case 'return_warehouse_24h_unprocessed': statusFieldForContext = 'isFailedReturnedToWhsUnprocessedOver24h'; break;
                case 'v2:ALL': statusFieldForContext = 'isUndeliveredLc'; break;
                default: break;
            }
        }

        const initialStatuses = statusFieldForContext ? [statusFieldForContext] : [];
        const initialReasons = { undeliveredReasonLevel1: v2Decoded?.level1, undeliveredReasonLevel2: v2Decoded?.level2 };
        const initialDetailFilters = isDriverRecord
            ? { selectedDsps: initialDsps, selectedAreas: [], selectedDrivers: [String((record as DriverSlaData).driverId || UNASSIGNED_DRIVER_ID)], selectedStatuses: initialStatuses, ...initialReasons }
            : { selectedDsps: initialDsps, selectedAreas: (record as AreaSlaData).area ? [(record as AreaSlaData).area] : [], selectedDrivers: [], selectedStatuses: initialStatuses, ...initialReasons };
        setDetailFilters(initialDetailFilters);
        setLastDetailContext({ type, record, statusField: statusFieldForContext });

        setDetailPage(1);
        setDetailPageSize(10);
        setDetailLoading(true);
        setDrawerData([]);
        setDetailTotal(0);
        setDrawerVisible(true);

        try {
            const baseMetrics = {
                id: record.id,
                actualArrivals: record.actualArrivals,
                pickedCount: record.pickedCount,
                unpickedCount: record.unpickedCount,
                inProgress: record.inProgress,
                deliveredLatest: record.deliveredLatest,
                failedIncomplete: record.failedIncomplete,
                latestDeliveryRate: record.latestDeliveryRate,
                failedReturnedToWhsCount: record.failedReturnedToWhsCount,
                returnedParcelCount: record.returnedParcelCount,
                returnedParcelIncompleteOver72hRate: record.returnedParcelIncompleteOver72hRate,
                failedReturnedToWhsUnprocessedOver24hRate: record.failedReturnedToWhsUnprocessedOver24hRate,
                lostDamagedCount: record.lostDamagedCount,
                arrivalPointType: record.arrivalPointType,
                t1DeliveryRate: record.t1DeliveryRate,
                t2DeliveryRate: record.t2DeliveryRate,
                t3DeliveryRate: record.t3DeliveryRate,
                t4DeliveryRate: record.t4DeliveryRate,
                t5DeliveryRate: record.t5DeliveryRate,
            };
            const recordForFetch: AreaSlaData = isDriverRecord
                ? { ...baseMetrics, area: (record as DriverSlaData).driverName, areaId: 0, dsp: (record as DriverSlaData).dsp, dspId: (record as DriverSlaData).dspId, city: '' }
                : (record as AreaSlaData);

            const { items, total } = await fetchDetailData(
                type,
                recordForFetch,
                undefined,
                undefined,
                1,
                10,
                initialDetailFilters
            );
            setDrawerData(items);
            setDetailTotal(total);
        } catch (error) {
            console.error('获取详情数据失败:', error);
            setDrawerData([]);
            setDetailTotal(0);
        } finally {
            setDetailLoading(false);
        }
    };

    // 详情翻页（后端分页）
    const handleDetailPageChange = async (page: number, pageSize?: number) => {
        if (!lastDetailContext) return;
        
        setDetailLoading(true);
        // 如果 pageSize 未传入，使用当前 detailPageSize，但不应该小于10
        const size = pageSize ?? Math.max(detailPageSize, 10);
        setDetailPage(page);
        setDetailPageSize(size);
        const { type, record } = lastDetailContext;
        
        // 检查是否是异常预警类型（使用language-agnostic的type）
        const anomalyTypes = ['unpicked24h', 'undelivered24h', 'undelivered48h', 'undelivered72h', 'aboutToTimeout24h', 'aboutToTimeout48h', 'lostDamaged', 'noUpdate24h', 'noUpdate72h'];
        if (anomalyTypes.includes(type)) {
            // 使用抽屉内选择的状态筛选；清空时传 undefined 表示全选（与 hub-parcel-sla-monitoring 一致）
            const selectedStatuses = detailFilters.selectedStatuses;
            const statusField = selectedStatuses.length === 1 ? selectedStatuses[0] : undefined;
            const statusFields = selectedStatuses.length > 1 ? selectedStatuses : undefined;

            // 根据详情筛选条件构建查询参数，与dailyTask保持一致（不回退到主筛选条件）
            const selectedDspIdsFromDetailFilter = detailFilters.selectedDsps.length > 0
                ? detailFilters.selectedDsps.map(dspName => {
                    const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                    return dsp ? dsp.id : null;
                }).filter(id => id !== null) as number[]
                : undefined;

            // 如果是DSP经理，自动添加DSP过滤条件
            const finalDspIds = isDsp && dspId ? [parseInt(dspId)] : selectedDspIdsFromDetailFilter;

            const selectedAreaIds = detailFilters.selectedAreas.length > 0
                ? detailFilters.selectedAreas.map(areaName => {
                    const area = areas.find((a: any) => a.name === areaName);
                    return area ? (typeof area.id === 'number' ? area.id : parseInt(String(area.id), 10)) : null;
                }).filter((id): id is number => id !== null)
                : undefined;

            // 详情筛选条件：司机ID；选中「司机为 -」时仅查未分配司机
            const detailDriverUnassignedOnly = isUnassignedDriverOnly(detailFilters.selectedDrivers);
            const selectedDriverIds = detailDriverUnassignedOnly ? undefined : toDriverIds(detailFilters.selectedDrivers);

            // 一级页面选中的城市，传给详情接口
            const selectedCityNames = filters.selectedCities?.length ? filters.selectedCities : undefined;

            // 构建分页请求（带上一级页的 whs、日期、地区等）
            const hasDetailDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
            const detailStart = hasDetailDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
            const detailEnd = hasDetailDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
            const pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType = {
                filter: {
                    arrivalDateStart: detailStart,
                    arrivalDateEnd: detailEnd,
                    warehouseIds: warehouseId ? [Number(warehouseId)] : [],
                    dspIds: finalDspIds,
                    areaIds: selectedAreaIds,
                    driverIds: selectedDriverIds,
                    driverIdNullOnly: detailDriverUnassignedOnly ? true : undefined,
                    cityNames: selectedCityNames,
                    trackingNumber: undefined,
                    statusField,
                    statusFields,
                    arrivalPointType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS),
                    undeliveredReasonLevel1: detailFilters.undeliveredReasonLevel1,
                    undeliveredReasonLevel2: detailFilters.undeliveredReasonLevel2,
                },
                page: page - 1,
                size: size,
                sortBy: detailSortConfig.sortBy,
                sortDirection: detailSortConfig.sortDirection
            };

            try {
                const { data: detailResponse } = await client.query({
                    query: isDsp ? GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST : GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
                    variables: { pageRequest }
                });

                const detailResp = isDsp
                    ? detailResponse?.getDeliveryProgressMonitoringOfSlaDetailListForDsp
                    : detailResponse?.getDeliveryProgressMonitoringOfSlaDetailList;
                const detailItems = detailResp?.content || [];
                const totalElements = detailResp?.totalElements || 0;

                // 转换数据格式
                const convertedData: DetailData[] = detailItems.map((item: any) => ({
                    id: item.id,
                    dsp_name: (item.lastOperateDspName ?? item.lastAssignDspName) || '',
                    area_name: item.areaName || '',
                    driver_id: item.lastOperateDriverId ?? item.lastAssignDriverId ?? 0,
                    driver_name: (item.lastOperateDriverName ?? item.lastAssignDriverName) || '',
                    last_operate_dsp_name: item.lastOperateDspName || '',
                    last_operate_driver_name: item.lastOperateDriverName || '',
                    last_assign_dsp_name: item.lastAssignDspName || '',
                    last_assign_driver_name: item.lastAssignDriverName || '',
                    task_date: item.taskDate || '',
                    tracking_number: item.trackingNumber,
                    status: item.overallStatusText || item.overallStatus?.toString() || '',
                    undelivered_reason_level_1: item.undeliveredReasonLevel1 || undefined,
                    undelivered_reason_level_2: item.undeliveredReasonLevel2 || undefined,
                    order_id: item.orderId
                }));

                setDrawerData(convertedData);
                setDetailTotal(totalElements);
                // 确保分页大小与请求一致，使用我们请求的 size
                // 不依赖后端返回的 size，因为可能不正确
                setDetailPageSize(size);
            } catch (error) {
                console.error('获取异常详情数据失败:', error);
            }
        } else {
            // 普通详情分页处理
            const { items, total } = await fetchDetailData(type, record as AreaSlaData | DriverSlaData, undefined, undefined, page, size);
            setDrawerData(items);
            setDetailTotal(total);
        }
        
        setDetailLoading(false);
    };

    // 点击异常预警查看详情（prefill 用于从二级 breakdown 页点击数字带入 DSP/司机）
    const handleAnomalyClick = async (
        anomalyType: string,
        prefill?: { selectedDsps?: string[]; selectedAreas?: string[]; selectedDrivers?: string[] }
    ) => {
        // 重置详情分页状态到第一页，默认页面大小为10
        const initialPageSize = 10;
        setDetailPage(1);
        setDetailPageSize(initialPageSize);

        // v2 未妥投归因：'v2:ALL' 打开抽屉不预填 L1/L2（"全部未妥投"）；
        // 'v2:L1[:L2]' 解码后作为 L1/L2 预填。命中时 status 不预填（v2 走独立筛选字段）。
        const v2Prefix = anomalyType.startsWith('v2:');
        const v2Decoded = v2Prefix && anomalyType !== 'v2:ALL' ? decodeUndeliveredAnomaly(anomalyType) : null;
        const undeliveredL1: UndeliveredLevel1 | undefined = v2Decoded?.level1;
        const undeliveredL2: UndeliveredLevel2 | undefined = v2Decoded?.level2;

        // 根据异常类型确定状态字段（用于默认状态筛选）
        const statusFieldForAnomaly = (() => {
            if (v2Prefix) return undefined;
            switch (anomalyType) {
                case 'actualArrivals': return 'isActualArrivals';
                case 'picked': return 'isPicked';
                case 'deliveredLatest': return 'isDeliveredLatest';
                case 'missScanWhsArrival': return 'isMissScanWhsArrival';
                case 'unpicked24h': return 'isUnpicked24h';
                case 'unpicked12h': return 'isUnpicked12h';
                case 'undelivered24h': return 'isUndelivered24h';
                case 'undelivered48h': return 'isUndelivered48h';
                case 'undelivered72h': return 'isUndelivered72h';
                case 'aboutToTimeout24h': return 'isAboutToTimeout24h';
                case 'aboutToTimeout48h': return 'isAboutToTimeout48h';
                case 'noUpdate24h': return 'isNoUpdate24h';
                case 'noUpdate72h': return 'isNoUpdate72h';
                case 'noUpdate48To72h': return 'isNoUpdate48To72h';
                case 'noUpdate48To72hWarehouse': return 'isNoUpdate48To72hWarehouse';
                case 'noUpdate48To72hDriver': return 'isNoUpdate48To72hDriver';
                case 'noUpdateOver72h': return 'isNoUpdateOver72h';
                case 'noUpdateOver72hWarehouse': return 'isNoUpdateOver72hWarehouse';
                case 'noUpdateOver72hDriver': return 'isNoUpdateOver72hDriver';
                case 'unpicked': return 'isUnpicked';
                case 'inDelivery': return 'isInDelivery';
                case 'failedNotReturned': return 'isFailedIncomplete';
                case 'failedReturnedToWhs': return 'isFailedReturnedToWhs';
                case 'returnedParcel': return 'isReturnedParcel';
                case 'lostDamaged': return 'isLostDamaged';
                // 汇总区分子下钻：退件72小时未完结 / 返仓24小时未处理
                case 'returnIncomplete72h': return 'isReturnedParcelIncompleteOver72h';
                case 'returnWarehouse24hUnprocessed': return 'isFailedReturnedToWhsUnprocessedOver24h';
                default: return undefined;
            }
        })();

        // 初始化详情筛选条件：有 prefill 时用 prefill（来自二级页），否则用主筛选条件
        const initialDetailFilters = prefill
            ? {
                selectedDsps: prefill.selectedDsps ?? [],
                selectedAreas: prefill.selectedAreas ?? [],
                selectedDrivers: prefill.selectedDrivers ?? [],
                selectedStatuses: statusFieldForAnomaly ? [statusFieldForAnomaly] : [],
                undeliveredReasonLevel1: undeliveredL1,
                undeliveredReasonLevel2: undeliveredL2,
            }
            : {
                selectedDsps: filters.selectedDsps.length > 0 ? filters.selectedDsps : [],
                selectedAreas: filters.selectedAreas.length > 0 ? filters.selectedAreas : [],
                selectedDrivers: [] as string[],
                selectedStatuses: statusFieldForAnomaly ? [statusFieldForAnomaly] : [],
                undeliveredReasonLevel1: undeliveredL1,
                undeliveredReasonLevel2: undeliveredL2,
            };
        setDetailFilters(initialDetailFilters);

        // 根据type获取翻译后的label用于显示
        const anomalyTypeLabels: { [key: string]: string } = {
            'actualArrivals': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isActualArrivals'),
            'picked': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isPicked'),
            'deliveredLatest': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isDeliveredLatest'),
            'missScanWhsArrival': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isMissScanWhsArrival'),
            'unpicked24h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.notPickedUp24h'),
            'undelivered24h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.firstPickup24hNotDelivered'),
            'undelivered48h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.firstPickup48hNotDelivered'),
            'undelivered72h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.firstPickup72hNotDelivered'),
            'aboutToTimeout24h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.approachingTimeout24h'),
            'aboutToTimeout48h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.approachingTimeout48h'),
            'lostDamaged': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isLostDamaged'),
            'noUpdate24h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.noTracking24h'),
            'noUpdate72h': t('wms.delivery.parcelSlaMonitoring.exceptionAlert.noTracking72h'),
            'noUpdate48To72h': t('wms.delivery.parcelSlaMonitoring.break48to72h'),
            'noUpdate48To72hWarehouse': `${t('wms.delivery.parcelSlaMonitoring.break48to72h')}（${t('wms.delivery.parcelSlaMonitoring.warehouseSide')}）`,
            'noUpdate48To72hDriver': `${t('wms.delivery.parcelSlaMonitoring.break48to72h')}（${t('wms.delivery.parcelSlaMonitoring.driverSide')}）`,
            'noUpdateOver72h': t('wms.delivery.parcelSlaMonitoring.break72h'),
            'noUpdateOver72hWarehouse': `${t('wms.delivery.parcelSlaMonitoring.break72h')}（${t('wms.delivery.parcelSlaMonitoring.warehouseSide')}）`,
            'noUpdateOver72hDriver': `${t('wms.delivery.parcelSlaMonitoring.break72h')}（${t('wms.delivery.parcelSlaMonitoring.driverSide')}）`,
            'unpicked': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isUnpicked'),
            'inDelivery': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isInDelivery'),
            'failedNotReturned': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isFailedIncomplete'),
            'failedReturnedToWhs': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isFailedReturnedToWhs'),
            'returnedParcel': t('wms.delivery.parcelSlaMonitoring.detailStatusOptions.isReturnedParcel')
            ,
            // 汇总区分子下钻
            'returnIncomplete72h': t('wms.delivery.parcelSlaMonitoring.detailDrawer.returnIncomplete72hDrillTitle'),
            'returnWarehouse24hUnprocessed': t('wms.delivery.parcelSlaMonitoring.detailDrawer.returnWarehouse24hDrillTitle')
        };
        const v2Label = v2Prefix
            ? (anomalyType === 'v2:ALL'
                ? t('wms.delivery.parcelSlaMonitoring.detailDrawer.allUndeliveredDrillTitle', { defaultValue: '全部未妥投' })
                : (v2Decoded
                    ? (v2Decoded.level2
                        ? `${getLevel1Label(t, v2Decoded.level1)} - ${getLevel2Label(t, v2Decoded.level2)}`
                        : getLevel1Label(t, v2Decoded.level1))
                    : null))
            : null;
        const anomalyLabel = v2Label ?? anomalyTypeLabels[anomalyType] ?? anomalyType;

        // 根据筛选条件设置抽屉标题（已移除城市筛选条件）
        if (filters.selectedDsps.length > 1 || filters.selectedAreas.length > 1) {
            setDrawerTitle(`${anomalyLabel} - ${t('wms.delivery.parcelSlaMonitoring.detailDrawer.detailPage')} (${t('wms.delivery.parcelSlaMonitoring.detailDrawer.multipleFilters')})`);
        } else {
            setDrawerTitle(`${anomalyLabel} - ${t('wms.delivery.parcelSlaMonitoring.detailDrawer.detailPage')}`);
        }
        
        // 使用统一的列定义函数，支持排序
        const columns = getColumnsByType(anomalyLabel, handleDetailSortChange);
        setDrawerColumns(columns);
        
        const statusField = statusFieldForAnomaly;

        // 如果汇总为 0，则直接显示空结果，避免"汇总为0但详情有数据"的错觉
        // 注意：anomalySummary 已移除，不再进行汇总检查
        if (false) {
            const countMap: Record<string, number> = {};
            if ((countMap[anomalyType] || 0) === 0) {
                setDrawerColumns(columns);
                setDrawerData([]);
                setDrawerVisible(true);
                return;
            }
        }

        // 根据详情筛选条件构建查询参数，使用刚刚设置的 initialDetailFilters 而不是旧的 detailFilters
        // 名称→id 走累积缓存：initialDetailFilters 是本帧刚构造的，选项数组还没并入这些已选项
        const selectedDspIdsFromDetailFilter = initialDetailFilters.selectedDsps.length > 0
            ? initialDetailFilters.selectedDsps
                .map(dspName => dspIdByName.current.get(dspName) ?? null)
                .filter(id => id !== null) as number[]
            : undefined;

        // 如果是DSP经理，自动添加DSP过滤条件
        const finalDspIds = isDsp && dspId ? [parseInt(dspId)] : selectedDspIdsFromDetailFilter;

        const selectedAreaIds = initialDetailFilters.selectedAreas.length > 0
            ? initialDetailFilters.selectedAreas
                .map(areaName => areaIdByName.current.get(areaName) ?? null)
                .filter((id): id is number => id !== null)
            : undefined;

        // 详情筛选条件：司机ID；选中「司机为 -」时仅查未分配司机
        const initialDriverUnassignedOnly = isUnassignedDriverOnly(initialDetailFilters.selectedDrivers);
        const selectedDriverIds = initialDriverUnassignedOnly ? undefined : toDriverIds(initialDetailFilters.selectedDrivers);

        // 一级页面选中的城市，传给详情接口
        const selectedCityNames = filters.selectedCities?.length ? filters.selectedCities : undefined;

        // 构建分页请求（带上一级页的 whs、日期、地区等，使详情与统计一致）
        const hasInitialDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const initialStart = hasInitialDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const initialEnd = hasInitialDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType = {
            filter: {
                arrivalDateStart: initialStart,
                arrivalDateEnd: initialEnd,
                warehouseIds: warehouseId ? [Number(warehouseId)] : [],
                dspIds: finalDspIds,
                areaIds: selectedAreaIds,
                driverIds: selectedDriverIds,
                driverIdNullOnly: initialDriverUnassignedOnly ? true : undefined,
                cityNames: selectedCityNames,
                trackingNumber: undefined,
                statusField,
                statusFields: undefined,
                arrivalPointType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS),
                undeliveredReasonLevel1: undeliveredL1,
                undeliveredReasonLevel2: undeliveredL2,
            },
            page: 0,
            size: initialPageSize,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        const virtualRecord: AreaSlaData = {
            id: 'anomaly',
            area: '异常预警',
            areaId: 0,
            dsp: '',
            dspId: 0,
            city: '',
            actualArrivals: 0,
            pickedCount: 0,
            unpickedCount: 0,
            inProgress: 0,
            deliveredLatest: 0,
            failedIncomplete: 0,
            latestDeliveryRate: 0,
            failedReturnedToWhsCount: 0,
            returnedParcelCount: 0,
            lostDamagedCount: 0,
            returnedParcelIncompleteOver72hRate: 0,
            failedReturnedToWhsUnprocessedOver24hRate: 0,
            t1DeliveryRate: 0,
            t2DeliveryRate: 0,
            t3DeliveryRate: 0,
            t4DeliveryRate: 0,
            t5DeliveryRate: 0
        };
        setLastDetailContext({ type: anomalyType, record: virtualRecord, statusField });
        setDetailLoading(true);
        setDrawerData([]);
        setDetailTotal(0);
        setDrawerVisible(true);

        try {
            // 直接调用详情查询API
            const { data: detailResponse } = await client.query({
                query: isDsp ? GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST : GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
                variables: { pageRequest }
            });

            const detailResp = isDsp
                ? detailResponse?.getDeliveryProgressMonitoringOfSlaDetailListForDsp
                : detailResponse?.getDeliveryProgressMonitoringOfSlaDetailList;
            const detailItems = detailResp?.content || [];
            const totalElements = detailResp?.totalElements || 0;

                // 转换数据格式（必须包含 id 作为 rowKey，避免同 tracking_number 时重复渲染）
            const convertedData: DetailData[] = detailItems.map((item: any) => ({
                id: item.id,
                dsp_name: (item.lastOperateDspName ?? item.lastAssignDspName) || '',
                area_name: item.areaName || '',
                driver_id: item.lastOperateDriverId ?? item.lastAssignDriverId ?? 0,
                driver_name: (item.lastOperateDriverName ?? item.lastAssignDriverName) || '',
                last_operate_dsp_name: item.lastOperateDspName || '',
                last_operate_driver_name: item.lastOperateDriverName || '',
                last_assign_dsp_name: item.lastAssignDspName || '',
                last_assign_driver_name: item.lastAssignDriverName || '',
                task_date: item.taskDate || '',
                tracking_number: item.trackingNumber,
                status: item.overallStatusText || item.overallStatus?.toString() || '',
                undelivered_reason_level_1: item.undeliveredReasonLevel1 || undefined,
                undelivered_reason_level_2: item.undeliveredReasonLevel2 || undefined,
                order_id: item.orderId
            }));

            setDrawerData(convertedData);
            setDetailTotal(totalElements); // 设置正确的总数
            // 确保分页大小与请求一致，使用我们设置的 initialPageSize（10）
            // 不依赖后端返回的 size，因为可能不正确
            setDetailPageSize(initialPageSize);
            // 重新设置列定义，确保排序功能正常
            setDrawerColumns(getColumnsByType(anomalyType, handleDetailSortChange));
        } catch (error) {
            console.error('获取异常详情数据失败:', error);
            setDrawerData([]);
            setDetailTotal(0);
        } finally {
            setDetailLoading(false);
        }
    };

    // 请求断更司机侧 breakdown 某一页数据（共用变量构建）
    const fetchBreakdownPage = async (anomalyType: string, page: number, pageSizeOverride?: number) => {
        const size = pageSizeOverride ?? breakdownPageSize;
        const selectedDspIds = filters.selectedDsps.length > 0
            ? filters.selectedDsps.map(dspName => {
                const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                return dsp ? dsp.id : null;
            }).filter(id => id !== null) as number[]
            : undefined;
        const finalDspIds = isDsp && dspId ? [parseInt(dspId)] : selectedDspIds;
        const selectedAreaIds = filters.viewMode === DIMENSION_AREA && filters.selectedAreas.length > 0
            ? filters.selectedAreas.map(areaName => {
                const area = areas.find((a: any) => a.name === areaName);
                return area ? (typeof area.id === 'number' ? area.id : parseInt(String(area.id), 10)) : null;
            }).filter((id): id is number => id !== null)
            : undefined;
        const selectedDriverIds = filters.viewMode === DIMENSION_DRIVER ? toDriverIds(filters.selectedDrivers) : undefined;
        const arrivalPointType = (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS);
        const hasDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
        const arrivalDateStart = hasDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const arrivalDateEnd = hasDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
        const breakType: NoUpdateBreakType = anomalyType === 'noUpdate48To72h' ? 'NO_UPDATE_48_TO_72H' : 'NO_UPDATE_OVER_72H';
        const { data } = await client.query({
            query: isDsp ? GET_DSP_BREAK_DRIVER_SIDE_BREAKDOWN : GET_BREAK_DRIVER_SIDE_BREAKDOWN,
            variables: {
                breakType,
                filter: {
                    arrivalDateStart,
                    arrivalDateEnd,
                    warehouseIds: warehouseId ? [Number(warehouseId)] : [],
                    dspIds: finalDspIds,
                    areaIds: selectedAreaIds,
                    driverIds: selectedDriverIds,
                    cityNames: filters.selectedCities?.length ? filters.selectedCities : undefined,
                    arrivalPointType,
                    dimension: filters.viewMode,
                },
                page,
                size,
            }
        });
        const res = isDsp ? data?.getBreakDriverSideBreakdownForDsp : data?.getBreakDriverSideBreakdown;
        if (res) {
            setBreakdownData(res.content ?? []);
            setBreakdownPage(res.page ?? 1);
            setBreakdownTotal(res.totalElements ?? 0);
            setBreakdownTotalPages(res.totalPages ?? 0);
            if (pageSizeOverride !== undefined) {
                setBreakdownPageSize(pageSizeOverride);
            }
        }
    };

    // 断更「司机侧」点击：先打开二级页（DSP/司机数量 breakdown），再在二级页点击数字打开明细抽屉
    const handleDriverSideBreakdownClick = async (anomalyType: string) => {
        setBreakdownAnomalyType(anomalyType);
        setBreakdownTitle(
            String(
                anomalyType === 'noUpdate48To72h'
                    ? t('wms.delivery.parcelSlaMonitoring.breakdown.aboutToBreakTitle')
                    : t('wms.delivery.parcelSlaMonitoring.breakdown.alreadyBreakTitle')
            )
        );
        setBreakdownData([]);
        setBreakdownDrawerVisible(true);
        setBreakdownLoading(true);
        try {
            await fetchBreakdownPage(anomalyType, 1);
            setBreakdownDrawerVisible(true);
        } catch (error) {
            console.error('获取断更司机侧 breakdown 失败:', error);
            setBreakdownData([]);
            setBreakdownDrawerVisible(true);
        } finally {
            setBreakdownLoading(false);
        }
    };

    const handleBreakdownPageChange = (page: number) => {
        setBreakdownLoading(true);
        fetchBreakdownPage(breakdownAnomalyType, page)
            .catch((error) => console.error('获取断更司机侧 breakdown 分页失败:', error))
            .finally(() => setBreakdownLoading(false));
    };

    const handleBreakdownPageSizeChange = (size: number) => {
        setBreakdownLoading(true);
        fetchBreakdownPage(breakdownAnomalyType, 1, size)
            .catch((error) => console.error('获取断更司机侧 breakdown 分页失败:', error))
            .finally(() => setBreakdownLoading(false));
    };

    const handleBreakdownRowClick = (anomalyType: string, dspId: number, dspName: string, driverId?: number, driverName?: string) => {
        // 二级页的 DSP/司机名称同样不一定在搜索选项里，先记下行上的 id 供下游反查
        if (dspName && dspId) dspIdByName.current.set(dspName, dspId);
        if (driverName && driverId) driverNameById.current.set(Number(driverId), driverName);

        // 不关闭二级抽屉，仅打开明细抽屉，便于关掉包裹详情后仍能看到按 DSP 分组的二级页
        // 司机侧二级页点进详情时使用司机侧状态字段
        const detailAnomalyType = anomalyType === 'noUpdate48To72h' ? 'noUpdate48To72hDriver' : anomalyType === 'noUpdateOver72h' ? 'noUpdateOver72hDriver' : anomalyType;
        handleAnomalyClick(detailAnomalyType, {
            selectedDsps: [dspName],
            selectedAreas: [],
            selectedDrivers: driverId ? [String(driverId)] : []
        });
    };

    // 监听排序状态变化，重新生成列定义
    useEffect(() => {
        if (drawerVisible && drawerTitle) {
            const titleParts = drawerTitle.split(' - ');
            if (titleParts.length >= 2) {
                const type = titleParts[0]; // 类型在第一个部分
                const newColumns = getColumnsByType(type, handleDetailSortChange);
                setDrawerColumns(newColumns);
            }
        }
    }, [detailSortConfig.sortBy, detailSortConfig.sortDirection, drawerVisible, drawerTitle]);

    // 监听详情筛选条件变化，重新查询详情数据
    useEffect(() => {
        if (drawerVisible && lastDetailContext) {
            const { type, record } = lastDetailContext;
            setDetailPage(1);
            setDetailLoading(true);
            fetchDetailData(type, record as AreaSlaData | DriverSlaData, detailSortConfig.sortBy, detailSortConfig.sortDirection, 1, detailPageSize)
                .then(({ items, total }) => {
                    setDrawerData(items);
                    setDetailTotal(total);
                })
                .catch((error) => {
                    console.error('详情筛选条件变化后重新获取数据失败:', error);
                })
                .finally(() => {
                    setDetailLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(detailFilters)]);

    // 稳定化预设值字符串，确保预设变化时能触发查询
    const arrivalTimeDatePresetStr = useMemo(() => {
        return filters.arrivalTimeDatePreset ?? null;
    }, [filters.arrivalTimeDatePreset]);

    // 初始化查询和分页查询（与 getDeliveryProgressMonitoringOfSlaPageList 一致：filter 用 listFilter，仅加分页）
    useEffect(() => {
        if (!warehouseId) return;
        const pageRequest: DeliveryProgressMonitoringOfSlaPageRequestType = {
            filter: listFilter,
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: sortConfig.sortBy,
                sortDirection: sortConfig.sortDirection,
            }
        };
        handleFilter(pageRequest);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        warehouseId,
        listFilter,
        currentPage,
        pageSize,
        sortConfig.sortBy,
        sortConfig.sortDirection,
    ]);

    // 分页处理函数
    const handlePageChange = (page: number, newPageSize?: number) => {
        setCurrentPage(page);
        if (newPageSize && newPageSize !== pageSize) {
            setPageSize(newPageSize);
        }
    };

    // 处理排序变化
    const handleSortChange = (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
        // 更新排序状态
        setSortConfig({ sortBy, sortDirection });
        // 重置到第一页
        setCurrentPage(1);
    };

    // 处理详情排序变化
    const handleDetailSortChange = async (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
        // 更新排序状态
        setDetailSortConfig({ sortBy, sortDirection });
        
        // 如果抽屉是打开的，重新获取数据
        if (drawerVisible && drawerData.length > 0) {
            // 获取当前详情类型（从抽屉标题推断）
            const titleParts = drawerTitle.split(' - ');
            
            if (titleParts.length >= 2) {
                // 异常预警标题格式: "超24小时未领取 - 详情页"
                // 正常详情标题格式: "区域A - 实际到件 - 详情页"
                let type: string;
                if (titleParts.length === 2) {
                    // 异常预警格式: "超24小时未领取 - 详情页"
                    type = titleParts[0];
                } else if (titleParts.length === 3) {
                    // 正常详情格式: "区域A - 实际到件 - 详情页"
                    type = titleParts[1];
                } else {
                    type = titleParts[0];
                }
                
                // 检查是否是异常预警
                if (ANOMALY_TYPE_MAP[type]) {
                    // 重新调用异常预警查询
                    try {
                        // 优先使用详情筛选条件，与dailyTask保持一致
                        const selectedDspIdsFromDetailFilter = detailFilters.selectedDsps.length > 0
                            ? detailFilters.selectedDsps.map(dspName => {
                                const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[]
                            : undefined;
                        
                        const selectedDspIdsFromFilter = filters.selectedDsps.length > 0 
                            ? filters.selectedDsps.map(dspName => {
                                const dsp = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[]
                            : undefined;

                        // 如果是DSP经理，自动添加DSP过滤条件
                        const selectedDspIds = isDsp && dspId 
                            ? [parseInt(dspId)] 
                            : (selectedDspIdsFromDetailFilter || selectedDspIdsFromFilter);

                        const selectedAreaIdsFromDetailFilter = detailFilters.selectedAreas.length > 0
                            ? detailFilters.selectedAreas.map(areaName => {
                                const area = areas.find((a: any) => a.name === areaName);
                                return area ? (typeof area.id === 'number' ? area.id : parseInt(String(area.id), 10)) : null;
                            }).filter((id): id is number => id !== null)
                            : undefined;

                        const selectedAreaIds = selectedAreaIdsFromDetailFilter
                            || (filters.selectedAreas.length > 0
                                ? filters.selectedAreas.map(areaName => {
                                    const area = areas.find((a: any) => a.name === areaName);
                                    return area ? (typeof area.id === 'number' ? area.id : parseInt(String(area.id), 10)) : null;
                                }).filter((id): id is number => id !== null)
                                : undefined);

                        // 一级页面选中的城市，传给详情接口
                        const selectedCityNames = filters.selectedCities?.length ? filters.selectedCities : undefined;

                        // 详情筛选条件：司机ID；选中「司机为 -」时仅查未分配司机
                        const breakdownDriverUnassignedOnly = isUnassignedDriverOnly(detailFilters.selectedDrivers);
                        const selectedDriverIds = breakdownDriverUnassignedOnly ? undefined : toDriverIds(detailFilters.selectedDrivers);

                        // 清空状态时不传 status，后端返回所有状态（与 hub-parcel-sla-monitoring 一致）
                        const selectedStatuses = detailFilters.selectedStatuses;
                        const statusField = selectedStatuses.length === 1 ? selectedStatuses[0] : undefined;
                        const statusFields = selectedStatuses.length > 1 ? selectedStatuses : undefined;
                        const hasBreakdownDateRange = filters.arrivalTimeDateRange?.[0] && filters.arrivalTimeDateRange?.[1];
                        const breakdownStart = hasBreakdownDateRange ? filters.arrivalTimeDateRange![0].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
                        const breakdownEnd = hasBreakdownDateRange ? filters.arrivalTimeDateRange![1].format('YYYY-MM-DD') : filters.arrivalDate.format('YYYY-MM-DD');
                        const pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType = {
                            filter: {
                                arrivalDateStart: breakdownStart,
                                arrivalDateEnd: breakdownEnd,
                                warehouseIds: warehouseId ? [Number(warehouseId)] : [],
                                dspIds: selectedDspIds,
                                areaIds: selectedAreaIds,
                                driverIds: selectedDriverIds,
                                driverIdNullOnly: breakdownDriverUnassignedOnly ? true : undefined,
                                cityNames: selectedCityNames,
                                trackingNumber: undefined,
                                statusField,
                                statusFields,
                arrivalPointType: (filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS),
                                undeliveredReasonLevel1: detailFilters.undeliveredReasonLevel1,
                                undeliveredReasonLevel2: detailFilters.undeliveredReasonLevel2,
                            },
                            page: detailPage - 1,
                            size: detailPageSize,
                            sortBy: sortBy,
                            sortDirection: sortDirection
                        };

                        const { data: detailResponse } = await client.query({
                            query: isDsp ? GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST : GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
                            variables: { pageRequest }
                        });

                        const detailResp = isDsp
                            ? detailResponse?.getDeliveryProgressMonitoringOfSlaDetailListForDsp
                            : detailResponse?.getDeliveryProgressMonitoringOfSlaDetailList;
                        const detailItems = detailResp?.content || [];
                        const convertedData: DetailData[] = detailItems.map((item: any) => ({
                            id: item.id,
                            dsp_name: (item.lastOperateDspName ?? item.lastAssignDspName) || '',
                            area_name: item.areaName || '',
                            driver_id: item.lastOperateDriverId ?? item.lastAssignDriverId ?? 0,
                            driver_name: (item.lastOperateDriverName ?? item.lastAssignDriverName) || '-',
                            last_operate_dsp_name: item.lastOperateDspName || '',
                            last_operate_driver_name: item.lastOperateDriverName || '',
                            last_assign_dsp_name: item.lastAssignDspName || '',
                            last_assign_driver_name: item.lastAssignDriverName || '',
                            task_date: item.taskDate || '',
                            tracking_number: item.trackingNumber,
                            status: item.overallStatusText || item.overallStatus?.toString() || '',
                            undelivered_reason_level_1: item.undeliveredReasonLevel1 || undefined,
                            undelivered_reason_level_2: item.undeliveredReasonLevel2 || undefined,
                            order_id: item.orderId
                        }));
                        
                        setDrawerData(convertedData);
                    } catch (error) {
                        console.error('重新获取异常详情数据失败:', error);
                    }
                } else if (NORMAL_TYPE_MAP[type]) {
                    // 重新获取普通详情数据
                    const detailType = NORMAL_TYPE_MAP[type];
                    try {
                        // 使用保存的原始详情上下文数据
                        const defaultRecord: AreaSlaData = {
                            id: '',
                            area: titleParts[0],
                            areaId: 0,
                            dsp: '',
                            dspId: 0,
                            city: '',
                            actualArrivals: 0,
                            pickedCount: 0,
                            unpickedCount: 0,
                            inProgress: 0,
                            deliveredLatest: 0,
                            failedIncomplete: 0,
                            latestDeliveryRate: 0,
                            failedReturnedToWhsCount: 0,
                            returnedParcelCount: 0,
                            lostDamagedCount: 0,
                            returnedParcelIncompleteOver72hRate: 0,
                            failedReturnedToWhsUnprocessedOver24hRate: 0,
                            t1DeliveryRate: 0,
                            t2DeliveryRate: 0,
                            t3DeliveryRate: 0,
                            t4DeliveryRate: 0,
                            t5DeliveryRate: 0
                        };
                        const originalRecord: SlaTableRow = lastDetailContext?.record ?? defaultRecord;

                        const { items } = await fetchDetailData(detailType, originalRecord as AreaSlaData | DriverSlaData, sortBy, sortDirection);
                        setDrawerData(items);
                    } catch (error) {
                        console.error('重新获取详情数据失败:', error);
                    }
                }
            }
        }
    };

    // 处理过滤器更新
    const handleFiltersUpdate = (newFilters: SlaMonitoringFilters) => {
        setCurrentPage(1);
        setFilters(newFilters);
    };

    // 切换司机/区域维度：仅重置维度相关的筛选（DSP/区域/司机），保留日期类型和日期
    const handleViewModeChange = (mode: SlaMonitoringViewMode) => {
        setSortConfig({
            sortBy: 'overallConversionRate',
            sortDirection: 'DESC',
        });
        setFilters({
            ...filters,
            viewMode: mode,
            selectedDsps: [],
            selectedAreas: [],
            selectedDrivers: [],
            selectedCities: [],
        });
        setCurrentPage(1);
    };

    // 区域/司机共用同一接口，分页、loading、汇总、记录均来自单次查询
    const currentPagination = apiPagination;
    const currentLoading = loading;
    const currentSummaryData = summaryData;

    // 数据更新时间（用于页面展示：数据更新于 XXX）——使用当前维度数据的 calculatedAt，统一转为浏览器本地时间
    const [dataUpdatedAtText, setDataUpdatedAtText] = useState<string>('');
    useEffect(() => {
        const calculatedAt = recordData.length > 0 ? recordData[0]?.calculatedAt : null;
        if (!calculatedAt) {
            setDataUpdatedAtText('');
            return;
        }
        try {
            const d = dayjs.utc(calculatedAt);
            setDataUpdatedAtText(d.isValid() ? d.local().format('YYYY-MM-DD HH:mm:ss') : String(calculatedAt));
        } catch {
            setDataUpdatedAtText(String(calculatedAt));
        }
    }, [recordData]);

    return {
        filters,
        setFilters: handleFiltersUpdate,
        areaData,
        driverData,
        viewMode: filters.viewMode,
        handleViewModeChange,
        anomalyData,
        summaryData: currentSummaryData,
        dsps,
        areas,
        drivers,
        // DSP/区域/司机下拉改为远程搜索，交给筛选组件驱动关键词
        onDspSearch: setDspKeyword,
        onAreaSearch: setAreaKeyword,
        onDriverSearch: setDriverKeyword,
        dspOptionsLoading,
        areaOptionsLoading,
        driverOptionsLoading,
        onDrawerDriverSearch: setDrawerDriverKeyword,
        drawerDriverOptionsLoading,
        detailDrawerDsps,
        detailDrawerAreas,
        detailDrawerDrivers,
        drawerDrivers,
        drawerVisible,
        drawerTitle,
        drawerData,
        drawerColumns,
        setDrawerVisible,
        handleDetailClick,
        handleAnomalyClick,
        breakdownDrawerVisible,
        setBreakdownDrawerVisible,
        breakdownData,
        breakdownTitle,
        breakdownAnomalyType,
        breakdownLoading,
        breakdownPage,
        breakdownPageSize,
        breakdownTotal,
        breakdownTotalPages,
        handleDriverSideBreakdownClick,
        handleBreakdownPageChange,
        handleBreakdownPageSizeChange,
        handleBreakdownRowClick,
        detailFilters,
        setDetailFilters,
        loading: currentLoading,
        error,
        pagination: currentPagination ? {
            current: currentPagination.page,
            pageSize: currentPagination.size,
            total: currentPagination.totalElements,
        } : null,
        tablePagination: currentPagination ? {
            page: currentPagination.page,
            size: currentPagination.size,
            totalElements: currentPagination.totalElements,
            totalPages: currentPagination.totalPages,
            hasNext: currentPagination.hasNext,
            hasPrevious: currentPagination.hasPrevious,
        } : undefined,
        handlePageChange,
        handleSortChange,
        handleDetailSortChange,
        // 详情分页与翻页
        detailPagination: { page: detailPage, size: detailPageSize, total: detailTotal },
        handleDetailPageChange,
        detailSortConfig,
        detailLoading,
        orderDetailVisible,
        orderId,
        setOrderDetailVisible,
        showOrderDetail,
        warehouseId,
        lastDetailContext,
        dspMode: isDsp,
        currentDspId: dspId,
        dataUpdatedAtText,
    };
};
